import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { IChildMenuOptionImplicitList } from './types';
import style from './style.module.scss';
import { IServicePaymentOption, IBookingMenuOption, IWidgetTheme } from 'shared-types/index';
import MenuOption from '../MenuOption';
import { menuOptionType } from '../MenuOption/types';
import CoverInput from 'external-components/cover-input/index';
import { coverInputVariant } from 'external-components/cover-input/types';
import IframeResizerService from 'external-services/iframe-resizer-service';
import BookingOptionsHelpers from 'external-components/booking-options-section/helpers';
import { debounce } from 'lodash-es';

const NS = 'ChildMenuOptionImplicitList';

export default function ChildMenuOptionImplicitList({
  theme,
  menuOptions,
  currency,
  wrapperStyle,
  selectedMenuOptions,
  gridSizes,
  gridGutter,
  isUpsell,
  handleSpinnerUpdate,
  isDesignV2,
  stateType,
  disableBookingUpsellOptions,
}: IChildMenuOptionImplicitList) {
  const [combinedCovers, setCombinedCovers] = useState<IBookingMenuOption[]>([]);

  useEffect(() => {
    const updatedCombinedCovers = menuOptions
      ? menuOptions.map((opt) => {
          const selectedOpt = !selectedMenuOptions
            ? null
            : selectedMenuOptions.find((o) => o.menuOptionId === opt.id);
          return { menuOptionId: opt.id, quantity: selectedOpt ? selectedOpt.quantity : 0 };
        })
      : [];
    setCombinedCovers(updatedCombinedCovers);
  }, [selectedMenuOptions]);

  const isStacked = IframeResizerService.isStacked(wrapperStyle);
  const useGrid = !!gridGutter || isUpsell;

  const handleMultiBookingOptionUpdateDebouncedHandler = useMemo(() => {
    return debounce((query) => {
      handleSpinnerUpdate(query);
    }, 500);
  }, []);

  const handleSpinnerChange = useCallback((selectedMenuOptions) => {
    handleMultiBookingOptionUpdateDebouncedHandler(selectedMenuOptions);
  }, []);

  const gridSizesDesignV2 = { xs: true, sm: false, md: true, lg: false };
  return (
    <div className={style.root}>
      {(() =>
        BookingOptionsHelpers.muiGridOrCustomElementOuter(
          isDesignV2 ? false : useGrid,
          style.grid,
          isDesignV2 ? style.boxV2 : style.box,
          gridGutter,
          <>
            {menuOptions &&
              menuOptions.map(
                ({ id, label, description, price, link }: IServicePaymentOption, index) =>
                  BookingOptionsHelpers.muiGridOrCustomElementInner(
                    id,
                    useGrid,
                    isDesignV2
                      ? gridSizesDesignV2
                      : {
                          xs: 6,
                          sm: gridSizes[1],
                          md: gridSizes[2],
                          lg: gridSizes[3],
                        },
                    '',
                    isDesignV2 ? style.boxItemsV2 : style.boxItems,
                    null,
                    <div className={style.listItem} data-testid={`menu-option`} data-test-uid={id}>
                      <MenuOption
                        isStacked={isStacked}
                        id={id}
                        stateType={stateType}
                        theme={theme}
                        currency={currency}
                        price={price}
                        label={label}
                        description={description}
                        link={link}
                        type={menuOptionType.spinners}
                        isUpsell={isUpsell}
                        isDesignV2={isDesignV2}
                        extrasHasPayment={false}
                        isLastItem={index === menuOptions.length - 1}
                      >
                        {(() => {
                          const menuOpt: IBookingMenuOption = combinedCovers.find(
                            (cc) => cc.menuOptionId === id
                          );
                          return (
                            <CoverInput
                              theme={theme as IWidgetTheme}
                              wrapperStyle={wrapperStyle}
                              hasDelay={false}
                              coversPrefill={menuOpt?.quantity || 0}
                              maxPeoplePerBooking={1000}
                              minPeoplePerBooking={0}
                              disableControls={disableBookingUpsellOptions}
                              variant={
                                isDesignV2
                                  ? coverInputVariant.smallDesignV2
                                  : coverInputVariant.small
                              }
                              omitMaxReachedMsg={true}
                              allowMaxBreach={false}
                              handleChange={(quantity) => {
                                const newValue = combinedCovers.map((item: IBookingMenuOption) =>
                                  item.menuOptionId === id ? { menuOptionId: id, quantity } : item
                                );
                                setCombinedCovers(newValue);
                                handleSpinnerChange(newValue);
                              }}
                            />
                          );
                        })()}
                      </MenuOption>
                    </div>
                  )
              )}
          </>
        ))()}
    </div>
  );
}
